// The Payment Administration Fee's rate on a LIVE loan (BLA Core §2.4) — the
// answer to "what is the number", which was findable nowhere on the loan
// record. The fee's EFFECT already had surfaces (the buyer schedule's
// conditional column, the B3 statement review row, the standing worksheet's
// projected split); the RATE that produces it had none, so on a loan that does
// NOT carry the fee, and on a fee-carrying loan's balloon, nothing on any
// screen said so.
//
// 🔴 ITS OWN CARD, and specifically NOT either of the two places it looks like
// it belongs. Both rejections are load-bearing:
//
//  1. NOT a block inside BuyerConsultingFeeTermsCard. That card's closing line
//     is "Carved from the seller's distribution — the buyer's payment amount
//     does not change." This fee is the exact opposite: it is charged to the
//     BORROWER, on top, and debited with the payment as one combined amount.
//     Filing a borrower charge under that sentence would make the card lie
//     about one of the two fees on it.
//
//  2. NOT a block inside LoanTermsCard's "Note fee terms". The nil semantics
//     there are right — nullable, where a nil means "the note has no such
//     clause" and renders an em-dash — but that block is EDITABLE through
//     UpdateLoanTermsRequest under `Loans.Manage`, and this rate has NO staff
//     write path at all: it is stamped once by LoanServicingClient at boarding
//     and carried across resale / right-size replacement loans. Rendering it
//     inside an editable form offers a door that does not exist.
//
// 🔴 THREE STATES, NOT TWO. This is the whole reason the card could not simply
// be cloned from its consulting sibling, whose columns are NOT NULL with a
// 0.00 off state:
//
//   null / undefined   the note carries no such term. The entire book boarded
//                      before the column existed, never backfilled, with NO
//                      fallback to the standard rate (Loan.PaymentAdministrationFeePct
//                      says why: a fee the borrower never agreed to must not
//                      appear because a constant existed). Renders an EM-DASH.
//   0                  the term exists and bills nothing. Renders "0%".
//   > 0                the rate.
//
// Collapsing the first onto the second — which a `?? 0` does, and which is the
// correct read for the consulting rates one card up — would tell a reader that
// the whole grandfathered book is on a 0% administration fee rather than on no
// such term. The entity comment warns that harmonizing the columns is a
// regression, not a cleanup; this card is where that distinction is visible.

import { Card } from "@/design-system/components/ui";
import { formatRateFractionAsPercent } from "@/lib/rateFormat";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";

/** The line that keeps this fee from being read as the consulting carve one
 *  card up. Rendered on EVERY state, including the no-term one — the
 *  disambiguation is what the line is for, and it matters MOST where a reader
 *  might confuse two adjacent fee cards, not only where a rate is live.
 *
 *  Worded as what the fee IS rather than what this loan does, so it stays true
 *  on all three states: on a null-rate loan it explains a fee this note does
 *  not carry, which is exactly what the sibling card does at 0%. */
const CHARGE_NOTE =
  "This fee is charged to the borrower on top of the scheduled payment and "
  + "debited with it as one combined amount — it is not a carve out of the "
  + "seller's distribution. Phoenix pays the third-party servicer out of it.";

/** What the rate bills on, in the engine's own vocabulary. The exclusions are
 *  named because they are the half a reader cannot see from a percentage — and
 *  the balloon exclusion in particular is enforced in three places (the bill,
 *  the WF1 P24 fires-on set, and the orchestrator's sizing gate). */
const BASIS =
  "Of each scheduled principal & interest payment. Never the balloon, a payoff, "
  + "a prepayment or any fee event.";

const NOT_A_TERM =
  "This note carries no administration fee — it was boarded before the term "
  + "existed, and there is deliberately no fallback to the standard rate.";

const NOT_CHARGED = "The term is on this note but bills nothing.";

interface Props {
  loan: LoanSummaryDto;
}

export function PaymentAdministrationFeeTermsCard({ loan }: Props) {
  const pct = loan.paymentAdministrationFeePct;
  // `undefined` (an older payload, or an inline fixture that omitted the field)
  // is treated exactly as `null`: unknown and no-such-term both render the
  // em-dash, and NEITHER may render a rate. Failing toward "no term" is the
  // honest direction — inventing 0% would assert a term that may not exist.
  const hasTerm = pct !== null && pct !== undefined;

  return (
    <Card data-testid="payment-administration-fee-terms-card">
      <div className="mb-space-3">
        <div className="font-display font-bold text-h3 text-onyx">
          Payment administration fee
        </div>
        <p className="text-caption text-gray-mid m-0">
          A term of the note, set at boarding
        </p>
      </div>

      <div>
        <div className="font-display text-caption uppercase tracking-label text-gray-mid">
          Rate
        </div>
        <div className="text-onyx mt-[2px] text-small">
          <span
            className="font-mono tabular-nums"
            data-testid="payment-administration-fee-pct"
          >
            {hasTerm ? formatRateFractionAsPercent(pct) : "—"}
          </span>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            {!hasTerm ? NOT_A_TERM : pct === 0 ? NOT_CHARGED : BASIS}
          </p>
        </div>
      </div>

      <p className="text-caption text-gray-mid m-0 mt-space-3">{CHARGE_NOTE}</p>
    </Card>
  );
}
